import React, { useState } from "react";
import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useNavigate } from "react-router-dom";

const ConservationStories = () => {
  const [activeStory, setActiveStory] = useState(0);
  const navigate = useNavigate();

  const stories = [
  {
    id: 1,
    category: "Species Recovery",
    title: "Philippine Cockatoo Population Rebounds",
    excerpt: "After 15 years of intensive conservation efforts, the critically endangered Philippine Cockatoo population has increased by 340% within the park boundaries. This success demonstrates the effectiveness of habitat protection and community engagement programs.",
    image: "https://images.unsplash.com/photo-1521054714586-a954df7c4695",
    imageAlt: "White Philippine cockatoo with distinctive yellow crest perched on tropical tree branch with lush green forest background showing successful species recovery",
    date: "December 5, 2025",
    impact: "+340% Population Growth",
    author: "Dr. Maria Santos",
    authorRole: "Chief Conservation Biologist"
  },
  {
    id: 2,
    category: "Habitat Restoration",
    title: "Mangrove Reforestation Milestone Achieved",
    excerpt: "Community volunteers and park rangers successfully planted 50,000 mangrove seedlings along the coastal buffer zone, creating critical habitat for juvenile fish species and strengthening natural storm protection for local communities.",
    image: "https://images.unsplash.com/photo-1715330396131-ee4b66113e60",
    imageAlt: "Dense mangrove forest with intricate root systems in shallow coastal waters reflecting successful reforestation efforts with clear blue sky above",
    date: "November 28, 2025",
    impact: "50,000 Trees Planted",
    author: "Ranger Carlos Mendoza",
    authorRole: "Habitat Restoration Lead"
  },
  {
    id: 3,
    category: "Community Impact",
    title: "Local Guides Earn UNESCO Certification",
    excerpt: "Twenty-five community members completed advanced training in sustainable tourism practices and received UNESCO certification as official park guides, creating economic opportunities while ensuring visitor experiences align with conservation goals.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_189c961b4-1765151405560.png",
    imageAlt: "Group of Filipino community guides in official uniforms standing proudly with UNESCO certificates in front of park entrance with tropical forest background",
    date: "November 15, 2025",
    impact: "25 Certified Guides",
    author: "Elena Rodriguez",
    authorRole: "Community Engagement Director"
  }];


  const handleNext = () => {
    setActiveStory((prev) => (prev + 1) % stories?.length);
  };

  const handlePrev = () => {
    setActiveStory((prev) => (prev - 1 + stories?.length) % stories?.length);
  };

  return (
    <section className="py-20 bg-[var(--color-card)]">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-[var(--color-secondary)]/10 rounded-full px-4 py-2 mb-4">
            <Icon name="Heart" size={20} color="var(--color-secondary)" />
            <span className="text-[var(--color-secondary)] text-sm font-medium">
              Conservation Success Stories
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-4">
            Protecting Paradise Together
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
            Discover how community collaboration and scientific innovation are preserving one of Earth's most precious ecosystems
          </p>
        </div>

        {/* Story Carousel */}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Image Section */}
            <div className="relative h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-conservation-lg">
              <Image
                src={stories?.[activeStory]?.image}
                alt={stories?.[activeStory]?.imageAlt}
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <span className="inline-block bg-[var(--color-accent)] text-black text-xs font-bold px-3 py-1 rounded-full mb-3">
                  {stories?.[activeStory]?.category}
                </span>
                <div className="text-[var(--color-accent)] text-2xl font-bold">
                  {stories?.[activeStory]?.impact}
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-3xl lg:text-4xl font-bold text-[var(--color-foreground)] mb-4">
                  {stories?.[activeStory]?.title}
                </h3>
                <p className="text-lg text-[var(--color-muted-foreground)] leading-relaxed">
                  {stories?.[activeStory]?.excerpt}
                </p>
              </div>

              {/* Author Info */}
              <div className="flex items-center space-x-4 pt-4 border-t border-[var(--color-border)]">
                <div className="w-12 h-12 bg-[var(--color-primary)] rounded-full flex items-center justify-center">
                  <Icon name="User" size={24} color="var(--color-foreground)" />
                </div>
                <div>
                  <div className="text-[var(--color-foreground)] font-medium">
                    {stories?.[activeStory]?.author}
                  </div>
                  <div className="text-sm text-[var(--color-muted-foreground)]">
                    {stories?.[activeStory]?.authorRole}
                  </div>
                </div>
                <div className="ml-auto text-sm text-[var(--color-muted-foreground)]">
                  {stories?.[activeStory]?.date}
                </div>
              </div>

              {/* CTA */}
              <Button
                variant="default"
                className="bg-[var(--color-accent)] hover:bg-[var(--color-accent)]/90 text-black font-semibold"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate("/community-engagement-platform")}>

                Read Full Story
              </Button>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center space-x-4 mt-8">
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-[var(--color-muted)] hover:bg-[var(--color-primary)] transition-colors flex items-center justify-center">

              <Icon name="ChevronLeft" size={24} color="var(--color-foreground)" />
            </button>
            <div className="flex space-x-2">
              {stories?.map((_, index) =>
              <button
                key={index}
                onClick={() => setActiveStory(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                index === activeStory ?
                "bg-[var(--color-accent)] w-8" :
                "bg-[var(--color-muted)]"}`
                } />

              )}
            </div>
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-[var(--color-muted)] hover:bg-[var(--color-primary)] transition-colors flex items-center justify-center">

              <Icon name="ChevronRight" size={24} color="var(--color-foreground)" />
            </button>
          </div>
        </div>
      </div>
    </section>);

};

export default ConservationStories;